import React, { useEffect, useRef } from 'react';
import { ArrowDown, Globe, TreePine, Wind } from 'lucide-react';

const Hero = () => {
  const canvasRef = useRef(null);

  // Particle animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationId;
    let particles = [];

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    class Particle {
      constructor() {
        this.reset();
      }
      reset() {
        this.x = Math.random() * canvas.width;
        this.y = canvas.height + Math.random() * 100;
        this.size = Math.random() * 3 + 1;
        this.speedY = Math.random() * 0.8 + 0.2;
        this.speedX = (Math.random() - 0.5) * 0.3;
        this.opacity = Math.random() * 0.5 + 0.2;
        this.color = Math.random() > 0.5 ? '16, 185, 129' : '245, 158, 11';
      }
      update() {
        this.y -= this.speedY;
        this.x += this.speedX;
        if (this.y < -10) this.reset();
      }
      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${this.color}, ${this.opacity})`;
        ctx.fill();
      }
    }

    for (let i = 0; i < 60; i++) {
      particles.push(new Particle());
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        p.update();
        p.draw();
      });
      animationId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="inicio" style={{
      minHeight: '100vh',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      background: `
        linear-gradient(135deg, rgba(5, 46, 22, 0.9) 0%, rgba(6, 95, 70, 0.7) 50%, rgba(12, 10, 9, 0.9) 100%),
        url('https://images.unsplash.com/photo-1518173946687-a4c036bc3c95?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80')
      `,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    }}>
      {/* Particle Canvas */}
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 2,
          pointerEvents: 'none',
        }}
      />

      {/* Content */}
      <div className="container" style={{
        position: 'relative',
        zIndex: 3,
        textAlign: 'center',
        maxWidth: '900px',
        paddingTop: '100px',
      }}>
        {/* Badge */}
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '10px',
          padding: '10px 24px',
          background: 'rgba(16, 185, 129, 0.15)',
          border: '1px solid rgba(16, 185, 129, 0.3)',
          borderRadius: '50px',
          fontSize: '0.9rem',
          color: 'var(--primary-300)',
          marginBottom: '32px',
          backdropFilter: 'blur(10px)',
          animation: 'fadeInDown 1s ease',
        }}>
          <Globe size={16} />
          Empresa Brasileña en Paraguay
        </div>

        {/* Title */}
        <h1 style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 'clamp(2.5rem, 6vw, 5rem)',
          fontWeight: 700,
          lineHeight: 1.1,
          marginBottom: '24px',
          animation: 'fadeInUp 1s ease 0.2s both',
        }}>
          Transformando el{' '}
          <span className="text-gradient">Futuro</span>{' '}
          del Carbono
        </h1>

        {/* Subtitle */}
        <p style={{
          fontSize: 'clamp(1rem, 2vw, 1.25rem)',
          color: 'var(--text-secondary)',
          maxWidth: '700px',
          margin: '0 auto 40px',
          lineHeight: 1.7,
          animation: 'fadeInUp 1s ease 0.4s both',
        }}>
          Líderes en créditos de carbono, compensación de emisiones y sostenibilidad ambiental 
          en el corazón de Alto Paraná. Tecnología brasileña, excelencia paraguaya.
        </p>

        {/* CTA Buttons */}
        <div style={{
          display: 'flex',
          gap: '16px',
          justifyContent: 'center',
          flexWrap: 'wrap',
          animation: 'fadeInUp 1s ease 0.6s both',
        }}>
          <button
            onClick={() => scrollToSection('#contacto')}
            style={{
              padding: '16px 36px',
              background: 'var(--gradient-primary)',
              color: 'white',
              borderRadius: '50px',
              fontWeight: 600,
              fontSize: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              border: 'none',
              cursor: 'pointer',
              boxShadow: 'var(--shadow-glow)',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = '0 0 60px rgba(16, 185, 129, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'var(--shadow-glow)';
            }}
          >
            <TreePine size={20} />
            Solicitar Proyecto
          </button>
          <button
            onClick={() => scrollToSection('#nosotros')}
            style={{
              padding: '16px 36px',
              background: 'transparent',
              color: 'var(--text-primary)',
              borderRadius: '50px',
              fontWeight: 600,
              fontSize: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              border: '2px solid rgba(255,255,255,0.15)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--primary-400)';
              e.currentTarget.style.color = 'var(--primary-400)';
              e.currentTarget.style.transform = 'translateY(-3px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)';
              e.currentTarget.style.color = 'var(--text-primary)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <Wind size={20} />
            Conocer Más
          </button>
        </div>

        {/* Stats Preview */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '48px',
          marginTop: '60px',
          animation: 'fadeInUp 1s ease 0.8s both',
        }} className="hero-stats">
          {[
            { value: '125K+', label: 'Créditos Verificados' },
            { value: '45K', label: 'Hectáreas Conservadas' },
            { value: '18', label: 'Proyectos Activos' },
          ].map((stat) => (
            <div key={stat.label} style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: '2rem',
                fontWeight: 800,
                background: 'var(--gradient-primary)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}>
                {stat.value}
              </div>
              <div style={{
                fontSize: '0.85rem',
                color: 'var(--text-muted)',
                marginTop: '4px',
              }}>
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll Indicator */}
      <div style={{
        position: 'absolute',
        bottom: '40px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 3,
        animation: 'bounce 2s infinite',
        cursor: 'pointer',
      }} onClick={() => scrollToSection('#nosotros')}>
        <ArrowDown size={28} style={{ color: 'var(--primary-400)' }} />
      </div>

      <style>{`
        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% { transform: translateX(-50%) translateY(0); }
          40% { transform: translateX(-50%) translateY(-15px); }
          60% { transform: translateX(-50%) translateY(-8px); }
        }
        @media (max-width: 768px) {
          .hero-stats {
            flex-direction: column !important;
            gap: 24px !important;
          }
        }
      `}</style>
    </section>
  );
};

export default Hero;
